/**
 * TrendPieChart · Module A 投放趋势对比 - 饼图
 *
 * 需求 §4.4.4：
 * - 展示各游戏该指标的**聚合/均值**占比（取 dimension_summary `list` 字段累加）
 * - tooltip：
 *   - title：游戏 icon + 游戏名（gameTooltipLabel）
 *   - 行左侧：指标 label（metricLabel）替代默认 seriesName（"series0"）
 *   - 行右侧：DBO_V.reviews(value) + (percent%)
 * - legend 带游戏 icon（rich 模式）
 * - label 显示「[icon] {name}: {value}({percent})」+ 横线分隔
 * - labelLayout 左右两侧分流避免重叠
 *
 * ⚠️ 数据获取内置：组件内部调用 `useTrendPieSummary`（接口 dimension_summary）。
 *    在 TrendCompare 里仅当 chartType === 'pie' 时挂载本组件 → 切到 pie 才发请求；
 *    切走时组件卸载 + `useCancelRequestHook` 自动取消 in-flight 请求。
 *
 * label / labelLayout 实现参考：
 * @see src/pages/opinion/pages/Overview/KeyMetrics/Community/LeftChart/Chart.tsx L117-177
 */
import React, { useMemo, useRef } from 'react';
import EChartCommon from '@op/components/EChartCommon';
import { formatChartParamsDate } from '@op/components/ChartCommon/formatter';
import { DBO_V } from '@op/utils/format';
import { ITrendSeries } from '../../types/trend';
import { TMetric } from '../../types/common';
import { IFilter } from '../../types/filter';
import { TGameMap } from '../../hooks/useGameMap';
import { useTrendPieSummary } from '../../hooks/useTrendCompare';
import {
  buildGameRich,
  gameRichLabel,
  gameTooltipLabel,
  pieGameLabel,
} from './utils';

export interface ITrendPieChartProps {
  filter: IFilter;
  metric: TMetric;
  /** 游戏元信息映射（cond.materials_config_games） */
  gameMap: TGameMap;
  /**
   * 当前指标 label（如 "Active Ads"），用于 tooltip 行左侧文案
   * 替代 echart 单 series 饼图默认的 "series0"
   */
  metricLabel: string;
  /** 图表高度 */
  height?: number;

  emptyTitleText: string;
}

interface IPieDatum {
  name: string; // game_id
  value: number;
}

/**
 * 从 series[].listValue（API `list` 聚合/均值）构建饼图数据
 */
function buildPieData(series: ITrendSeries[]): IPieDatum[] {
  return series.map((s) => ({
    name: s.game_id,
    value: Number.isFinite(s.listValue) ? (s.listValue as number) : 0,
  }));
}

export default function TrendPieChart({
  filter,
  metric,
  gameMap,
  metricLabel,
  height = 360,
  emptyTitleText,
}: ITrendPieChartProps) {
  const eChartsRef = useRef<any>(null);

  const { pieSeries: series, loading } = useTrendPieSummary({ filter, metric });

  const empty = useMemo(() => {
    return series.every((item) => !item.listValue);
  }, [series]);

  const { options, total } = useMemo(() => {
    let data = buildPieData(series);
    // 饼图按值从大到小排序（影响图例和扇区顺序）
    data = [...data].sort((a, b) => b.value - a.value);
    const totalSum = data.reduce((sum, d) => sum + d.value, 0);
    const gameRich = buildGameRich(series, gameMap, 16);
    // 图例按排序后的 data 顺序排列
    const legendData = data.map((d) => d.name);

    return {
      total: totalSum,
      options: {
        legend: {
          data: legendData,
          formatter: (gameId: string) => gameRichLabel(gameId, gameMap),
          textStyle: { rich: gameRich },
        },
        tooltip: {
          borderWidth: 0,
          borderRadius: 6,
          padding: 0,
          formatter: (params: any) =>
            formatChartParamsDate({
              title: metricLabel,
              params: [params],
              listTitleFunc: () => gameTooltipLabel(params?.name, gameMap),
              valueFormat: (val: number) => {
                const percent = totalSum
                  ? `${((val / totalSum) * 100).toFixed(2)}%`
                  : '0%';
                return `${DBO_V.reviews(val)} (${percent})`;
              },
            }),
        },
        series: [
          {
            type: 'pie',
            data,
            radius: ['40%', '70%'],
            label: pieGameLabel(gameMap, totalSum, 16),
            labelLayout: (params: any) => {
              const isLeft =
                params.labelRect.x <
                eChartsRef?.current?.getEchartsInstance?.()?.getWidth() / 2;
              const points = params.labelLinePoints;
              // Update the end point.
              points[2][0] = isLeft
                ? params.labelRect.x
                : params.labelRect.x + params.labelRect.width;
              return {
                labelLinePoints: points,
              };
            },
            emphasis: {
              focus: 'self',
              label: {
                opacity: 0.2,
              },
              labelLine: {
                lineStyle: {
                  opacity: 0.2,
                },
              },
            },
            blur: {
              label: { opacity: 0.2 },
              itemStyle: {
                opacity: 0.2,
              },
              labelLine: {
                lineStyle: {
                  opacity: 0.2,
                },
              },
            },
          },
        ],
      },
    };
  }, [series, gameMap, metricLabel]);

  return (
    <EChartCommon
      ref={eChartsRef}
      options={options}
      optionsProps={{
        type: 'pie',
      }}
      height={height}
      loading={loading}
      emptyTitleText={emptyTitleText}
      hasEmptyDesc={false}
      empty={!loading && empty}
    />
  );
}
